// react
import { useEffect, useMemo, useRef } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box } from '@mui/material';

// relative
import { darkTheme, lightTheme } from './highchartsTheme';

// initialize module
import { useTheme } from '@emotion/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/stock';
import 'highcharts/modules/no-data-to-display';
import { DateTime } from 'luxon';

const toUTC = (timestamp) =>
  DateTime.fromISO(timestamp, { zone: 'utc' }).toMillis();

const getSeverityColor = (alarm) => {
  switch (alarm.Severity) {
    case 'Information':
      return 'hsla(200, 84%, 47%, 0.2)';
    case 'Trivial':
      return 'hsla(200, 84%, 47%, 0.2)';
    case 'Minor':
      return 'hsla(33, 97%, 55%, 0.2)';
    case 'Medium':
      return 'hsla(33, 97%, 55%, 0.2)';
    case 'Major':
      return 'hsla(0, 67%, 47%, 0.2)';
    case 'Critical':
      return 'hsla(0, 67%, 47%, 0.2)';
    default:
      return 'hsla(200, 84%, 47%, 0.2)';
  }
};

const getSeverityColor1 = (alarm) => {
  switch (alarm.Severity) {
    case 'Information':
      return 'hsla(200, 84%, 47%)';
    case 'Trivial':
      return 'hsla(200, 84%, 47%)';
    case 'Minor':
      return 'hsla(33, 97%, 55%)';
    case 'Medium':
      return 'hsla(33, 97%, 55%)';
    case 'Major':
      return 'hsla(0, 67%, 47%)';
    case 'Critical':
      return 'hsla(0, 67%, 47%)';
    default:
      return 'hsla(200, 84%, 47%)';
  }
};

const UnitAlarmChart = ({ alarm, unitData, isLoading }) => {
  const { t } = useTranslation('alarms');
  const chartRef = useRef(null);
  const theme = useTheme();

  // Use useMemo for chart options since Highcharts configuration is genuinely expensive
  const options = useMemo(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;

    // Set global theme options
    Highcharts.setOptions(themeOptions);

    const baseOptions = {
      lang: {
        noData: t('common.no_data_available'),
      },
      noData: {
        style: {
          fontWeight: 'normal',
          color: theme.palette.mode === 'dark' ? 'lightgray' : 'gray',
        },
      },
      loading: {
        labelStyle: {
          fontWeight: 'normal',
          color: theme.palette.mode === 'dark' ? 'white' : 'black',
        },
        style: {
          backgroundColor: 'rgba(255, 255, 255, 0)',
        },
        showDuration: 150,
        hideDuration: 150,
      },
      title: { text: null },
      chart: {
        height: 400,
        backgroundColor: 'transparent',
      },
      // navigator: { enabled: true, maskInside: true },
      events: {
        load() {
          const chart = this;

          // Ensure we don't attach multiple listeners (React rerenders / remounts)
          if (chart.__mouseleaveHandler) return;

          chart.__mouseleaveHandler = () => {
            chart.tooltip?.hide(0);
            chart.pointer?.reset?.(true); // clears hover state
            chart.xAxis?.forEach((a) => a.hideCrosshair?.());
            chart.yAxis?.forEach((a) => a.hideCrosshair?.());
          };

          chart.container.addEventListener(
            'mouseleave',
            chart.__mouseleaveHandler,
          );
        },
        destroy() {
          const chart = this;
          if (chart.__mouseleaveHandler) {
            chart.container.removeEventListener(
              'mouseleave',
              chart.__mouseleaveHandler,
            );
            chart.__mouseleaveHandler = null;
          }
        },
      },
      credits: { enabled: false },
      time: { useUTC: true },
      rangeSelector: { enabled: false },
      series: unitData || [],
      xAxis: {
        type: 'datetime',
        gridLineColor: themeOptions.xAxis.gridLineColor,
        lineColor: themeOptions.xAxis.lineColor,
        labels: {
          style: {
            color: themeOptions.xAxis.labels.style.color,
          },
        },
        plotBands: alarm
          ? [
              {
                color: getSeverityColor(alarm),
                from: toUTC(alarm.DataFrom),
                to: toUTC(alarm.DataTo),
                borderColor: getSeverityColor1(alarm),
                borderWidth: 2,
                borderRadius: 8,
                label: {
                  text: `${alarm.Title}`,
                  rotation: 0,
                  y: 20,
                  align: 'center',
                  verticalAlign: 'top',
                  style: {
                    color: theme.palette.text.primary,
                    fontWeight: '600',
                    fontSize: '12px',
                  },
                },
              },
            ]
          : [],
      },
      yAxis: {
        title: { text: t('charts.value') },
        gridLineColor: themeOptions.yAxis.gridLineColor,
        gridLineWidth: 0,
        labels: {
          style: {
            color: themeOptions.yAxis.labels.style.color,
          },
        },
      },
      tooltip: {
        shared: true,
        backgroundColor: themeOptions.tooltip.backgroundColor,
        borderColor: themeOptions.tooltip.borderColor,
        style: {
          color: theme.palette.mode === 'dark' ? 'white' : 'black',
        },
      },
      legend: {
        itemStyle: { color: themeOptions.legend.itemStyle.color },
      },
    };

    return baseOptions;
  }, [alarm, unitData, t, theme.palette.text.primary, theme.palette.mode]);

  // Imperative loading overlay
  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;
    if (isLoading) {
      chart.showLoading(`${t('common.loading_data')}`);
      chart.hideNoData();
    } else chart.hideLoading();
  }, [isLoading, t]);

  return (
    <Box sx={{ mt: 4 }}>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
        immutable={true}
      />
    </Box>
  );
};

export default UnitAlarmChart;
